import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Play, User, Video } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useInterviews } from "@/hooks/useInterviews";
import { fetchInterviewVideos, getDefaultThumbnail } from "@/lib/strapi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const INTERVIEW_FILTERS = [
  { id: "articles", label: "Articles" },
  { id: "videos", label: "Videos" },
];

const VIDEO_CHANNEL = "@cynergysports";
const ARTICLE_LIMIT = 12;
const VIDEO_LIMIT = 12;

const mergeVideoInterviews = (currentVideos, incomingVideos) => {
  const seenVideoIds = new Set(currentVideos.map((video) => video.videoId || video.id));
  const uniqueIncomingVideos = incomingVideos.filter((video) => {
    const key = video.videoId || video.id;
    if (!key || seenVideoIds.has(key)) {
      return false;
    }

    seenVideoIds.add(key);
    return true;
  });

  return [...currentVideos, ...uniqueIncomingVideos];
};

export default function InterviewsPage() {
  const {
    interviews,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useInterviews(ARTICLE_LIMIT);
  const [activeFilter, setActiveFilter] = useState("articles");
  const [videoInterviews, setVideoInterviews] = useState([]);
  const [isVideosLoading, setIsVideosLoading] = useState(false);
  const [isLoadingMoreVideos, setIsLoadingMoreVideos] = useState(false);
  const [videoError, setVideoError] = useState("");
  const [hasRequestedVideos, setHasRequestedVideos] = useState(false);
  const [nextVideoPageToken, setNextVideoPageToken] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const fallbackThumbnail = getDefaultThumbnail();

  const isArticlesView = activeFilter === "articles";
  const isPreparingVideos = !hasRequestedVideos && isVideosLoading;

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  const getInterviewPath = (interview) => `/interviews/${interview?.slug || interview?.id}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeFilter !== "videos" || hasRequestedVideos) {
      return undefined;
    }

    let mounted = true;

    const loadVideoInterviews = async () => {
      try {
        setIsVideosLoading(true);
        setVideoError("");
        const result = await fetchInterviewVideos({
          channel: VIDEO_CHANNEL,
          maxResults: VIDEO_LIMIT,
        });

        if (mounted) {
          setVideoInterviews(result.items || []);
          setNextVideoPageToken(result.nextPageToken || "");
        }
      } catch (err) {
        if (mounted) {
          setVideoError(err instanceof Error ? err.message : "Failed to load interview videos");
          setVideoInterviews([]);
          setNextVideoPageToken("");
        }
      } finally {
        if (mounted) {
          setHasRequestedVideos(true);
          setIsVideosLoading(false);
        }
      }
    };

    loadVideoInterviews();

    return () => {
      mounted = false;
    };
  }, [activeFilter, hasRequestedVideos]);

  useEffect(() => {
    if (activeFilter !== "videos") {
      setSelectedVideo(null);
    }
  }, [activeFilter]);

  const handleLoadMoreVideos = async () => {
    if (!nextVideoPageToken || isLoadingMoreVideos) {
      return;
    }

    try {
      setIsLoadingMoreVideos(true);
      setVideoError("");
      const result = await fetchInterviewVideos({
        channel: VIDEO_CHANNEL,
        maxResults: VIDEO_LIMIT,
        pageToken: nextVideoPageToken,
      });

      setVideoInterviews((currentVideos) => mergeVideoInterviews(currentVideos, result.items || []));
      setNextVideoPageToken(result.nextPageToken || "");
    } catch (err) {
      setVideoError(err instanceof Error ? err.message : "Failed to load more interview videos");
    } finally {
      setIsLoadingMoreVideos(false);
    }
  };

  return (
    <div data-testid="interviews-page" className="min-h-screen">
      <Header />
      <PageBanner
        eyebrow="Interviews"
        title="Voices of Athletes and Coaches"
        description="In-depth conversations with athletes, coaches, and leaders shaping the future of APAC."
        image="https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
      />

      <section className="bg-amber-pale py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between mb-10">
            <div className="max-w-2xl">
              <p className="font-inter text-sm uppercase tracking-[0.18em] text-amber-dusk mb-3">
                Browse Interviews
              </p>
              <p className="font-inter text-sm md:text-base text-warm-gray leading-relaxed">
                {/* {isArticlesView
                  ? "Articles are selected by default so readers can stay on the current interviews page and open any full story from here."
                  : "Video interviews are loaded from the CynergySports YouTube feed and open in a popup player right on this page."} */}
              </p>
            </div>

            <div
              className="inline-flex w-fit rounded-full border border-near-black/10 bg-white p-1 shadow-sm"
              role="tablist"
              aria-label="Interview content filters"
            >
              {INTERVIEW_FILTERS.map((filter) => {
                const isActive = activeFilter === filter.id;

                return (
                  <button
                    key={filter.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`rounded-full px-5 py-2.5 font-inter text-sm transition-colors duration-150 ${
                      isActive
                        ? "bg-near-black text-white"
                        : "text-warm-gray hover:text-near-black"
                    }`}
                    onClick={() => setActiveFilter(filter.id)}
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>

          {isArticlesView && (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                {isLoading && (
                  <div className="md:col-span-2 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                    Loading interviews...
                  </div>
                )}

                {!!error && !isLoading && interviews.length === 0 && (
                  <div className="md:col-span-2 bg-white rounded-sm p-6 text-sm font-inter text-red-600">
                    Unable to load interviews right now. Please try again shortly.
                  </div>
                )}

                {!isLoading && !error && interviews.length === 0 && (
                  <div className="md:col-span-2 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                    No interviews found.
                  </div>
                )}

                {!isLoading &&
                  interviews.map((interview, index) => (
                    <motion.div key={interview.id} variants={itemVariants}>
                      <Link
                        to={getInterviewPath(interview)}
                        data-testid={`interview-card-${interview.id}`}
                        className="group bg-white rounded-sm overflow-hidden border border-near-black/5 hover:-translate-y-1 transition-transform duration-200 flex flex-col md:flex-row h-full"
                      >
                        <div className="relative overflow-hidden md:w-2/5 flex-shrink-0 aspect-[16/10] md:aspect-auto">
                          <img
                            src={interview.image}
                            alt={interview.name}
                            loading={index < 2 ? "eager" : "lazy"}
                            decoding="async"
                            fetchPriority={index < 2 ? "high" : "low"}
                            onError={handleImageError}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <span className="absolute top-4 left-4 bg-solar-orange text-near-black font-inter font-semibold text-[11px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm">
                            {interview.type}
                          </span>
                        </div>

                        <div className="p-6 md:p-8 flex flex-col flex-1">
                          <div className="flex items-center gap-2 text-warm-gray mb-3">
                            <User size={12} />
                            <span className="font-inter font-medium text-xs text-near-black">{interview.name}</span>
                            {!!interview.role && (
                              <>
                                <span className="text-warm-gray/50">|</span>
                                <span className="font-inter font-light text-xs">{interview.role}</span>
                              </>
                            )}
                          </div>
                          <h3 className="font-inter font-semibold text-lg text-near-black tracking-tight leading-snug mb-2 group-hover:text-amber-dusk transition-colors duration-150">
                            {interview.title}
                          </h3>
                          <p className="font-inter font-normal text-sm text-warm-gray leading-relaxed flex-1 line-clamp-3">
                            {interview.teaser}
                          </p>
                          <div className="flex items-center justify-between mt-5">
                            <div className="flex items-center gap-2 text-warm-gray">
                              <Clock size={12} />
                              <span className="font-inter font-light text-xs">{interview.date}</span>
                            </div>
                            <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk group-hover:gap-2 transition-all duration-150">
                              Read
                              <ArrowUpRight size={14} />
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
              </motion.div>

              {!isLoading && interviews.length > 0 && (
                <div className="mt-10 flex flex-col items-center gap-4">
                  {!!error && (
                    <p className="font-inter text-sm text-red-600">
                      Unable to load more interviews right now. Please try again shortly.
                    </p>
                  )}

                  {hasMore && (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-full bg-near-black px-6 py-3 font-inter text-sm text-white transition-colors duration-150 hover:bg-amber-dusk disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={loadMore}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? "Loading more interviews..." : "Load More Interviews"}
                    </button>
                  )}

                  {!hasMore && !isLoadingMore && (
                    <p className="font-inter text-sm text-warm-gray">
                      You’ve reached the end of the interviews.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {!isArticlesView && (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
              >
                {isPreparingVideos && (
                  <div className="md:col-span-2 xl:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                    Loading interview videos...
                  </div>
                )}

                {!!videoError && !isPreparingVideos && videoInterviews.length === 0 && (
                  <div className="md:col-span-2 xl:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-red-600">
                    Unable to load interview videos right now. Please try again shortly.
                  </div>
                )}

                {!isPreparingVideos && !videoError && videoInterviews.length === 0 && (
                  <div className="md:col-span-2 xl:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                    No interview videos found.
                  </div>
                )}

                {!isPreparingVideos &&
                  videoInterviews.map((videoInterview) => (
                    <motion.div key={videoInterview.id} variants={itemVariants}>
                      <button
                        type="button"
                        data-testid={`interview-video-card-${videoInterview.id}`}
                        className="group w-full h-full text-left bg-white rounded-sm overflow-hidden border border-near-black/5 hover:-translate-y-1 transition-transform duration-200 flex flex-col"
                        onClick={() => setSelectedVideo(videoInterview)}
                      >
                        <div className="relative overflow-hidden aspect-video">
                          <img
                            src={videoInterview.image}
                            alt={videoInterview.title}
                            loading="lazy"
                            onError={handleImageError}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-near-black/25 group-hover:bg-near-black/35 transition-colors duration-200" />
                          <span className="absolute top-4 left-4 bg-solar-orange text-near-black font-inter font-semibold text-[11px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm">
                            Video
                          </span>
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-near-black shadow-lg transition-transform duration-200 group-hover:scale-110">
                              <Play size={22} className="ml-1" fill="currentColor" />
                            </span>
                          </span>
                        </div>

                        <div className="p-6 flex flex-col flex-1">
                          <div className="flex items-center gap-2 text-warm-gray mb-3">
                            <Video size={12} />
                            <span className="font-inter font-medium text-xs text-near-black">
                              {videoInterview.channelTitle || "CynergySports"}
                            </span>
                          </div>
                          <h3 className="font-inter font-semibold text-lg text-near-black tracking-tight leading-snug mb-2 group-hover:text-amber-dusk transition-colors duration-150 line-clamp-2">
                            {videoInterview.title}
                          </h3>
                          <p className="font-inter font-normal text-sm text-warm-gray leading-relaxed flex-1 line-clamp-3">
                            {videoInterview.teaser || "Watch this interview on the CynergySports YouTube channel."}
                          </p>
                          <div className="flex items-center justify-between mt-5">
                            <div className="flex items-center gap-2 text-warm-gray">
                              <Clock size={12} />
                              <span className="font-inter font-light text-xs">
                                {videoInterview.date || "Latest upload"}
                              </span>
                            </div>
                            <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk group-hover:gap-2 transition-all duration-150">
                              Watch
                              <ArrowUpRight size={14} />
                            </span>
                          </div>
                        </div>
                      </button>
                    </motion.div>
                  ))}
              </motion.div>

              {!isPreparingVideos && videoInterviews.length > 0 && (
                <div className="mt-10 flex flex-col items-center gap-4">
                  {!!videoError && (
                    <p className="font-inter text-sm text-red-600">
                      Unable to load more interview videos right now. Please try again shortly.
                    </p>
                  )}

                  {!!nextVideoPageToken && (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-full bg-near-black px-6 py-3 font-inter text-sm text-white transition-colors duration-150 hover:bg-amber-dusk disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={handleLoadMoreVideos}
                      disabled={isLoadingMoreVideos}
                    >
                      {isLoadingMoreVideos ? "Loading more videos..." : "Load More Videos"}
                    </button>
                  )}

                  {!nextVideoPageToken && !isLoadingMoreVideos && (
                    <p className="font-inter text-sm text-warm-gray">
                      You’ve reached the end of the video interviews.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Dialog open={Boolean(selectedVideo)} onOpenChange={(isOpen) => !isOpen && setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl overflow-hidden border-none bg-white p-0">
          {selectedVideo && (
            <>
              <div className="bg-near-black">
                <div className="aspect-video">
                  <iframe
                    src={selectedVideo.embedUrl}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </div>

              <div className="px-6 py-5">
                <DialogTitle className="font-inter text-xl text-near-black pr-12">
                  {selectedVideo.title}
                </DialogTitle>

                <div className="mt-3 flex flex-wrap items-center gap-4 text-warm-gray">
                  {!!selectedVideo.channelTitle && (
                    <div className="inline-flex items-center gap-2">
                      <User size={12} />
                      <span className="font-inter font-light text-xs">{selectedVideo.channelTitle}</span>
                    </div>
                  )}
                  {!!selectedVideo.date && (
                    <div className="inline-flex items-center gap-2">
                      <Clock size={12} />
                      <span className="font-inter font-light text-xs">{selectedVideo.date}</span>
                    </div>
                  )}
                </div>

                <DialogDescription className="mt-4 font-inter text-sm text-warm-gray leading-relaxed">
                  {selectedVideo.description || "Interview video from the CynergySports YouTube channel."}
                </DialogDescription>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
